import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Button, Modal, Badge,
  Spinner, Alert, Card, Stack, Toast, ToastContainer, ProgressBar, Dropdown
} from 'react-bootstrap';
import { CircularProgress, Box } from '@mui/material';
import DataTable from '../../../components/DataTable/DataTable';
import CalificacionesProveedores from './CalificacionesProveedores';

const EvaluacionPage = () => {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [showEvaluacionModal, setShowEvaluacionModal] = useState(false);
  const [proveedorAEvaluar, setProveedorAEvaluar] = useState(null);
  const [evaluacionesHistorial, setEvaluacionesHistorial] = useState([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [evaluacionSeleccionada, setEvaluacionSeleccionada] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [rucProveedores, setRucProveedores] = useState({});
  const [ultimaEvaluacionPorProveedor, setUltimaEvaluacionPorProveedor] = useState({});
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0, pages: 1 });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [exportStatus, setExportStatus] = useState(null);
  const [progress, setProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, text: '', severity: 'success' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const isMounted = useRef(true);

  const mostrarToast = (mensaje, variante = 'success') => {
    setToastMessage(mensaje);
    setToastVariant(variante);
    setShowToast(true);
  };

  const getEstadoBadge = useCallback((estado) => {
    const map = {
      activo: 'success',
      pendiente: 'warning',
      finalizado: 'info',
      cancelado: 'secondary',
      incompleto: 'danger'
    };
    return (
      <Badge bg={map[estado] || 'secondary'} className="rounded-pill">
        {estado}
      </Badge>
    );
  }, []);

  const getTipoPersonaBadge = useCallback((tipo) => (
    <Badge bg={tipo === 'juridica' ? 'primary' : 'secondary'} className="rounded-pill">
      {tipo === 'juridica' ? 'Jurídica' : 'Natural'}
    </Badge>
  ), []);

  const formatFechaConHora = useCallback((fechaString) => {
    if (!fechaString) return 'Sin fecha';
    const fecha = new Date(fechaString);
    if (isNaN(fecha.getTime())) return fechaString;
    const dia = fecha.getDate().toString().padStart(2, '0');
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const año = fecha.getFullYear();
    const horas = fecha.getHours().toString().padStart(2, '0');
    const minutos = fecha.getMinutes().toString().padStart(2, '0');
    return `${dia}/${mes}/${año} ${horas}:${minutos}`;
  }, []);

  const formatFechaCorta = useCallback((fechaString) => {
    if (!fechaString) return 'Sin fecha';
    const fecha = new Date(fechaString);
    if (isNaN(fecha.getTime())) return fechaString;
    const dia = fecha.getDate().toString().padStart(2, '0');
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const año = fecha.getFullYear();
    return `${dia}/${mes}/${año}`;
  }, []);

  const loadProveedores = useCallback(async (page = 1) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    setLoading(true);
    setIsSearching(true);

    try {
      const url = new URL('https://apiprov.conduto.com.ec/api/providers/proveedores-con-datos');
      url.searchParams.append('page', page);
      url.searchParams.append('per_page', itemsPerPage);

      if (searchTerm) url.searchParams.append('search', searchTerm);
      if (filterTipo) url.searchParams.append('tipo_persona', filterTipo);
      if (filterEstado) url.searchParams.append('estado', filterEstado);

      const res = await fetch(url, {
        credentials: 'include',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!res.ok) throw new Error('Error al cargar proveedores');

      const data = await res.json();

      if (isMounted.current) {
        setProveedores(data.usuarios || []);
        setPagination(data.pagination);

        const rucs = {};
        const ultimas = {};
        (data.usuarios || []).forEach(p => {
          if (p.ruc) rucs[p.id_usuario] = p.ruc;
          if (p.ultima_evaluacion) ultimas[p.id_usuario] = p.ultima_evaluacion;
        });
        setRucProveedores(rucs);
        setUltimaEvaluacionPorProveedor(ultimas);
      }
    } catch (err) {
      if (err.name !== 'AbortError' && isMounted.current) {
        setError(err.message);
        console.error('Error cargando proveedores:', err);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setIsSearching(false);
      }
    }
  }, [itemsPerPage, searchTerm, filterTipo, filterEstado]);

  const loadUserInfo = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const resUser = await fetch('https://apiprov.conduto.com.ec/api/protected-cookie', {
        credentials: 'include',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (resUser.ok && isMounted.current) {
        const userData = await resUser.json();
        setUserInfo(userData.usuario);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error cargando info de usuario:', err);
      }
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    const init = async () => {
      await loadUserInfo();
      await loadProveedores(1);
    };
    init();
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    loadProveedores(1);
  }, [itemsPerPage, searchTerm, filterTipo, filterEstado, loadProveedores]);

  useEffect(() => {
    if (currentPage !== pagination.page) {
      loadProveedores(currentPage);
    }
  }, [currentPage, loadProveedores, pagination.page]);

  const handleActualizarManual = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await loadProveedores(currentPage);
      setSnackbar({
        open: true,
        text: 'Tabla actualizada correctamente',
        severity: 'success'
      });
    } catch (err) {
      setError(err.message || 'Error al actualizar');
      setSnackbar({
        open: true,
        text: 'Error al actualizar la tabla',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [loadProveedores, currentPage]);

  const cargarHistorialEvaluaciones = useCallback(async (proveedorId) => {
    setCargandoHistorial(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const res = await fetch(
        `https://apiprov.conduto.com.ec/api/evaluaciones-proveedores/proveedor/${proveedorId}`,
        { credentials: 'include', signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (res.ok && isMounted.current) {
        const data = await res.json();
        const historialOrdenado = Array.isArray(data)
          ? data.sort((a, b) => new Date(b.fecha_creacion || b.fecha_evaluacion) - new Date(a.fecha_creacion || a.fecha_evaluacion))
          : [];
        setEvaluacionesHistorial(historialOrdenado);
      } else {
        setEvaluacionesHistorial([]);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && isMounted.current) {
        console.error('Error cargando historial:', error);
      }
      setEvaluacionesHistorial([]);
    } finally {
      setCargandoHistorial(false);
    }
  }, []);

  const abrirModalEvaluacion = useCallback(async (proveedor) => {
    setProveedorAEvaluar(proveedor);
    setEvaluacionSeleccionada(null);
    setModoEdicion(false);
    setShowEvaluacionModal(true);
    await cargarHistorialEvaluaciones(proveedor.id_usuario);
  }, [cargarHistorialEvaluaciones]);

  const cerrarModalEvaluacion = useCallback(() => {
    setShowEvaluacionModal(false);
    setProveedorAEvaluar(null);
    setEvaluacionesHistorial([]);
    setEvaluacionSeleccionada(null);
    setModoEdicion(false);
    setCargandoHistorial(false);
  }, []);

  const seleccionarEvaluacionHistorial = useCallback((Calificacion) => {
    setEvaluacionSeleccionada(Calificacion);
    setModoEdicion(false);
  }, []);

  const activarModoEdicion = useCallback(() => {
    setModoEdicion(true);
  }, []);

  const cancelarEdicion = useCallback(() => {
    setModoEdicion(false);
    if (evaluacionSeleccionada) {
      const evaluacionOriginal = evaluacionesHistorial.find(
        ev => ev.id_evaluacion === evaluacionSeleccionada.id_evaluacion
      );
      setEvaluacionSeleccionada(evaluacionOriginal);
    }
  }, [evaluacionSeleccionada, evaluacionesHistorial]);

  const manejarEvaluacionGuardada = useCallback(async (evaluacionActualizada) => {
    if (proveedorAEvaluar) {
      await cargarHistorialEvaluaciones(proveedorAEvaluar.id_usuario);
      await loadProveedores(currentPage);
    }
    if (modoEdicion) setModoEdicion(false);
  }, [proveedorAEvaluar, cargarHistorialEvaluaciones, modoEdicion, loadProveedores, currentPage]);

  const exportarASharepoint = useCallback(async () => {
    setExportingExcel(true);
    setExportStatus({ estado: 'preparando', registros: 0 });
    setProgress(10);

    try {
      setExportStatus({ estado: 'enviando', registros: 0 });
      setProgress(30);

      const response = await fetch('https://apiprov.conduto.com.ec/api/exportar/evaluaciones-con-nota', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          filtros: {
            search: searchTerm,
            tipo_persona: filterTipo,
            estado: filterEstado
          }
        })
      });

      setProgress(70);

      if (response.ok) {
        const result = await response.json();
        setProgress(100);
        setExportStatus({ estado: 'completado', registros: result.total_registros, mensaje: result.message });
        mostrarToast(result.message || 'Exportación completada exitosamente', 'success');
        if (result.url) window.open(result.url, '_blank');
      } else {
        const errorData = await response.json();
        setExportStatus({ estado: 'error', mensaje: errorData.detail });
        mostrarToast(errorData.detail || 'Error al exportar', 'error');
      }
    } catch (error) {
      console.error('Error en exportación:', error);
      setExportStatus({ estado: 'error', mensaje: error.message });
      mostrarToast('Error al exportar: ' + error.message, 'error');
    } finally {
      setExportingExcel(false);
      setTimeout(() => { setExportStatus(null); setProgress(0); }, 3000);
    }
  }, [searchTerm, filterTipo, filterEstado]);

  const getStatusMessage = () => {
    if (!exportStatus) return null;
    const messages = {
      'preparando': 'Preparando datos...',
      'enviando': 'Enviando al servidor...',
      'completado': '¡Exportación completada!',
      'error': 'Error en la exportación'
    };
    return messages[exportStatus.estado] || exportStatus.mensaje;
  };

  const columns = [
    {
      key: 'id_usuario',
      label: 'N°',
      sortable: false,
      render: (p) => {
        const index = proveedores.findIndex(user => user.id_usuario === p.id_usuario);
        return (currentPage - 1) * itemsPerPage + index + 1;
      }
    },
    {
      key: 'nombre',
      label: 'Proveedor',
      sortable: false,
      render: (p) => (
        <div>
          <div className="fw-medium">{p.nombre}</div>
          <small className="text-muted">@{p.usuario}</small>
        </div>
      )
    },
    {
      key: 'ultima_evaluacion',
      label: 'Última Evaluación',
      sortValue: (p) => p.ultima_evaluacion?.fecha_creacion || null,
      render: (p) => {
        const ultimaEval = p.ultima_evaluacion;
        if (ultimaEval?.fecha_creacion) {
          return <div>{formatFechaConHora(ultimaEval.fecha_creacion)}</div>;
        }
        return <small className="text-muted">Sin evaluaciones</small>;
      }
    },
    {
      key: 'ruc',
      label: 'RUC',
      sortable: false,
      render: (p) => (
        p.ruc
          ? <Badge bg="info" className="font-monospace">{p.ruc}</Badge>
          : <small className="text-muted">Sin RUC</small>
      )
    },
    {
      key: 'tipo_persona',
      label: 'Tipo',
      sortable: false,
      render: (p) => getTipoPersonaBadge(p.tipo_persona)
    },
    {
      key: 'estado',
      label: 'Estado',
      sortable: false,
      render: (p) => getEstadoBadge(p.estado)
    }
  ];

  const filterConfig = [
    {
      key: 'tipo_persona',
      label: 'Todos los tipos',
      options: [
        { value: 'natural', label: 'Persona Natural' },
        { value: 'juridica', label: 'Persona Jurídica' }
      ]
    },
    {
      key: 'estado',
      label: 'Todos los estados',
      options: [
        { value: 'activo', label: 'Activo' },
        { value: 'inactivo', label: 'Inactivo' }
      ]
    }
  ];

  const rowActions = useCallback((proveedor) => (
    <Stack direction="horizontal" gap={2} className="justify-content-end">
      <Button
        variant="primary"
        size="sm"
        onClick={() => abrirModalEvaluacion(proveedor)}
        disabled={!proveedor.ruc}
      >
        Evaluar
      </Button>
    </Stack>
  ), [abrirModalEvaluacion]);

  const mobileCardRenderer = useCallback((proveedor) => {
    const ultimaEval = proveedor.ultima_evaluacion;
    const index = proveedores.findIndex(p => p.id_usuario === proveedor.id_usuario);
    const secuencial = (currentPage - 1) * itemsPerPage + index + 1;

    return (
      <div>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="fw-bold text-primary">#{secuencial}</div>
          <div>
            {getTipoPersonaBadge(proveedor.tipo_persona)}
            <span className="ms-1">{getEstadoBadge(proveedor.estado)}</span>
          </div>
        </div>
        <div className="mb-2">
          <div className="fw-semibold fs-6">{proveedor.nombre}</div>
          <div className="text-muted small">@{proveedor.usuario}</div>
        </div>
        <div className="mb-2">
          <div className="d-flex align-items-center justify-content-between">
            <span className="fw-medium">Última Evaluación:</span>
            {ultimaEval?.fecha_creacion ? (
              <Badge bg="info" className="font-monospace">
                {formatFechaCorta(ultimaEval.fecha_creacion)}
              </Badge>
            ) : (
              <span className="text-muted small">Sin evaluaciones</span>
            )}
          </div>
          {ultimaEval?.calificacion_final && (
            <div className="small text-muted mt-1">
              Calificación: {ultimaEval.calificacion_final}%
            </div>
          )}
        </div>
        <div className="mb-3">
          <div className="d-flex align-items-center">
            <span className="fw-medium me-2">RUC:</span>
            {proveedor.ruc
              ? <Badge bg="info" className="font-monospace">{proveedor.ruc}</Badge>
              : <span className="text-muted small">Sin RUC</span>
            }
          </div>
        </div>
      </div>
    );
  }, [getTipoPersonaBadge, getEstadoBadge, formatFechaCorta, proveedores, currentPage, itemsPerPage]);

  const mobileActions = useCallback((proveedor) => (
    <div className="d-flex gap-2">
      <Button
        variant="primary"
        size="sm"
        onClick={() => abrirModalEvaluacion(proveedor)}
        disabled={!proveedor.ruc}
        className="flex-fill"
      >
        <i className="bi bi-clipboard-check me-1"></i>
        Evaluar
      </Button>
    </div>
  ), [abrirModalEvaluacion]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (newItemsPerPage) => setItemsPerPage(newItemsPerPage);
  const handleSearchChange = (value) => setSearchTerm(value);
  const handleFilterChange = (key, value) => {
    if (key === 'tipo_persona') setFilterTipo(value);
    if (key === 'estado') setFilterEstado(value);
  };
  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterTipo('');
    setFilterEstado('');
    setItemsPerPage(10);
    setCurrentPage(1);
  };

  const renderModalEvaluacion = useCallback(() => (
    <Modal
      show={showEvaluacionModal}
      onHide={cerrarModalEvaluacion}
      size="xl"
      fullscreen="lg-down"
      centered
    >
      <Modal.Header closeButton className="border-bottom py-2 py-md-3">
        <Modal.Title className="d-flex flex-column w-100">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <span className="fs-5 fs-md-4 fw-bold">
              Proveedor: {proveedorAEvaluar?.nombre}
            </span>
            <div className="d-flex gap-1 flex-wrap">
              {evaluacionSeleccionada && !modoEdicion && (
                <Badge bg="secondary" className="fs-7">Vista Previa</Badge>
              )}
              {modoEdicion && (
                <Badge bg="warning" text="dark" className="fs-7">Editando</Badge>
              )}
            </div>
          </div>
          <small className="mt-1">
            RUC: {proveedorAEvaluar?.ruc || 'Sin RUC'}
          </small>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0 d-flex flex-column" style={{ height: 'calc(100vh - 120px)' }}>
        <div className="d-flex flex-column flex-lg-row h-100">
          <div className="col-12 col-lg-4 border-end p-2 p-md-3 d-flex flex-column" style={{ maxHeight: '100%', overflow: 'auto' }}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h6 className="mb-0 fs-6 fw-semibold">Historial de Evaluaciones</h6>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => { setEvaluacionSeleccionada(null); setModoEdicion(false); }}
                className="flex-shrink-0"
              >
                <i className="bi bi-plus-lg d-lg-none"></i>
                <span className="d-none d-lg-inline">Nueva</span>
              </Button>
            </div>
            <div className="flex-grow-1 overflow-auto">
              {cargandoHistorial ? (
                <div className="text-center p-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <small className="text-muted">Cargando historial...</small>
                </div>
              ) : evaluacionesHistorial.length === 0 ? (
                <Card className="text-center p-3 border-light">
                  <small className="text-muted">No hay evaluaciones previas</small>
                </Card>
              ) : (
                evaluacionesHistorial.map((Calificacion) => (
                  <Card
                    key={Calificacion.id_evaluacion}
                    className="mb-2 cursor-pointer border border-light"
                    style={
                      evaluacionSeleccionada?.id_evaluacion === Calificacion.id_evaluacion
                        ? { backgroundColor: '#005793', color: '#fff', borderColor: '#005793' }
                        : { backgroundColor: 'rgb(233, 236, 239)' }
                    }
                    onClick={() => seleccionarEvaluacionHistorial(Calificacion)}
                  >
                    <Card.Body className="p-2">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1 me-2">
                          <small
                            className="fw-bold d-block"
                            style={evaluacionSeleccionada?.id_evaluacion === Calificacion.id_evaluacion ? { color: '#fff' } : {}}
                          >
                            {formatFechaConHora(Calificacion.fecha_actualizacion)}
                          </small>
                          <small style={evaluacionSeleccionada?.id_evaluacion === Calificacion.id_evaluacion ? { color: '#ffffff' } : { color: '#000000' }}>
                            Nota: {Calificacion.calificacion_final}
                          </small>
                        </div>
                        <Badge
                          className="flex-shrink-0"
                          bg={
                            Calificacion.estado_cumplimiento === 'CUMPLE' ? 'success' :
                              Calificacion.estado_cumplimiento === 'CUMPLE PARCIALMENTE' ? 'warning' :
                                Calificacion.estado_cumplimiento === 'NO CUMPLE' ? 'danger' : 'secondary'
                          }
                        >
                          {Calificacion.estado_cumplimiento?.split(' ')[0]}
                        </Badge>
                      </div>
                    </Card.Body>
                  </Card>
                ))
              )}
            </div>
          </div>
          <div className="col-12 col-lg-8 p-2 p-md-3 d-flex flex-column" style={{ maxHeight: '100%', overflow: 'auto' }}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                {evaluacionSeleccionada && (
                  <Badge bg="info" className="fs-7">
                    {formatFechaConHora(evaluacionSeleccionada.fecha_actualizacion)}
                  </Badge>
                )}
              </div>
              <div className="d-flex gap-2">
                {evaluacionSeleccionada && !modoEdicion && (
                  <Button variant="warning" size="sm" onClick={activarModoEdicion} className="d-flex align-items-center">
                    <i className="bi bi-pencil me-1 d-none d-sm-inline"></i>
                    <span>Editar</span>
                  </Button>
                )}
                {modoEdicion && (
                  <Button variant="outline-secondary" size="sm" onClick={cancelarEdicion} className="d-flex align-items-center">
                    <i className="bi bi-x-lg me-1"></i>
                    <span>Cancelar</span>
                  </Button>
                )}
              </div>
            </div>
            <div className="flex-grow-1 overflow-auto">
              <CalificacionesProveedores
                key={evaluacionSeleccionada?.id_evaluacion || 'nueva'}
                proveedorId={proveedorAEvaluar?.id_usuario}
                proveedorNombre={proveedorAEvaluar?.nombre}
                proveedorEmail={proveedorAEvaluar?.correo}
                tipoPersona={proveedorAEvaluar?.tipo_persona}
                evaluacionData={evaluacionSeleccionada}
                onEvaluacionCompletada={manejarEvaluacionGuardada}
                modoLectura={!!evaluacionSeleccionada && !modoEdicion}
                modoEdicion={modoEdicion}
                onCancelarEdicion={cancelarEdicion}
                userInfo={userInfo}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  ), [showEvaluacionModal, proveedorAEvaluar, evaluacionSeleccionada, modoEdicion, evaluacionesHistorial, cargandoHistorial, formatFechaConHora, seleccionarEvaluacionHistorial, activarModoEdicion, cancelarEdicion, manejarEvaluacionGuardada, userInfo, cerrarModalEvaluacion]);

  if (loading && proveedores.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container fluid className="px-4 py-4">
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          {error.includes('No autorizado') && (
            <p>No tienes permisos para ver esta información. Contacta al administrador.</p>
          )}
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <DataTable
        data={proveedores}
        loading={loading || isSearching}
        error={error}
        totalItems={pagination.total}
        title="Evaluación Anual de Proveedores"
        subtitle={`Mostrando ${proveedores.length} de ${pagination.total} proveedores`}
        columns={columns}
        filterConfig={filterConfig}
        rowActions={rowActions}
        mobileCardRenderer={mobileCardRenderer}
        mobileActions={mobileActions}
        headerActions={
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-primary"
              onClick={handleActualizarManual}
              className="d-flex align-items-center"
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <i className="bi bi-arrow-clockwise me-2"></i>
              )}
            </Button>
            <Dropdown>
              <Dropdown.Toggle variant="success" disabled={exportingExcel} className="d-flex align-items-center">
                {exportingExcel ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Exportando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-cloud-upload me-2"></i>
                    Exportar evaluaciones
                  </>
                )}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={exportarASharepoint} disabled={exportingExcel}>
                  <i className="bi bi-people me-2"></i>
                  Exportar evaluaciones de Proveedores
                  <br />
                  <small className="text-muted">Evaluaciones_Proveedor.xlsx</small>
                </Dropdown.Item>
                <Dropdown.Item
                  as="a"
                  href="https://ecconduto.sharepoint.com/sites/CondutoEcuador/CCC%20Compras%20Control%20Y%20Contrataciones/Forms/AllItems.aspx?id=%2Fsites%2FCondutoEcuador%2FCCC%20Compras%20Control%20Y%20Contrataciones%2FVARIOS%2FCALIFICACION%20DE%20PROVEEDORES%2FDOCUMENTOS%20PROVEEDORES%2FDatos%20Proveedor&viewid=5d40ac0a%2D125d%2D4cf3%2Da0e3%2D86858db31e93"
                  target="_blank"
                >
                  <i className="bi bi-eye me-2"></i>
                  Ver datos
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        }
        searchPlaceholder="Buscar proveedor, RUC, email..."
        searchValue={searchTerm}
        onSearchChange={handleSearchChange}
        filterValues={{ tipo_persona: filterTipo, estado: filterEstado }}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        itemsPerPageValue={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        currentPageValue={currentPage}
        onPageChange={handlePageChange}
        useServerPagination={true}
        externalPagination={{
          currentPage: currentPage,
          totalPages: pagination.pages,
          onPageChange: handlePageChange,
          onItemsPerPageChange: handleItemsPerPageChange
        }}
        snackbar={snackbar}
        onCloseSnackbar={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />

      {exportStatus && (
        <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1060 }}>
          <div className="bg-white border rounded shadow-sm p-3" style={{ minWidth: '300px' }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <strong>{getStatusMessage()}</strong>
                {exportStatus.registros && (
                  <div className="small text-muted">Registros: {exportStatus.registros}</div>
                )}
              </div>
              {exportStatus.estado === 'preparando' || exportStatus.estado === 'enviando' ? (
                <Spinner animation="border" size="sm" variant="primary" />
              ) : exportStatus.estado === 'completado' ? (
                <i className="bi bi-check-circle-fill text-success fs-4"></i>
              ) : exportStatus.estado === 'error' ? (
                <i className="bi bi-exclamation-circle-fill text-danger fs-4"></i>
              ) : null}
            </div>
            {progress > 0 && progress < 100 && (
              <ProgressBar now={progress} animated variant="success" style={{ height: '8px' }} />
            )}
          </div>
        </div>
      )}

      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1070 }}>
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={5000} autohide bg={toastVariant} className="border-0">
          <Toast.Body className={`d-flex align-items-center gap-2 ${toastVariant === 'success' ? 'text-white' : ''} p-3`}>
            {toastVariant === 'success' && <i className="bi bi-check-circle-fill fs-4"></i>}
            {toastVariant === 'danger' && <i className="bi bi-exclamation-circle-fill fs-4"></i>}
            {toastVariant === 'warning' && <i className="bi bi-exclamation-triangle-fill fs-4"></i>}
            <div>
              {toastMessage.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < toastMessage.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {renderModalEvaluacion()}
    </>
  );
};

export default EvaluacionPage;